import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Music, X, ChevronDown, Copy, Check, Link, Loader, ChevronUp } from "lucide-react";
import { createManualOrder } from "../../redux/reduxSlices/ordersSlice";
import { getUsersList } from "../../redux/reduxSlices/adminSlice";
import { songDuration } from "../../utils/util";

// Accordion Component
const Accordion = ({ title, children, isOpen = false, onToggle }) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 text-left"
        onClick={onToggle}
      >
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <ChevronDown
          className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const JingleOrderForm = ({ onClose, userRole, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [clientFormLink, setClientFormLink] = useState("");
  const [copied, setCopied] = useState(false);
  
  // Accordion states - all initially closed
  const [accordionStates, setAccordionStates] = useState({
    companyInfo: false,
    brandInfo: false,
    jingleRequirements: false,
    productionDetails: false,
  });

  const {
    adminLoading,
    adminFetchError,
    usersListResponse,
  } = useSelector((state) => state.admin);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getUsersList());
  }, []);

  // Toggle accordion
  const toggleAccordion = (accordion) => {
    setAccordionStates((prev) => ({
      ...prev,
      [accordion]: !prev[accordion],
    }));
  };

  // Add useEffect for closing dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      mobileNumber: "",
      emailAddress: "",
      companyName: "",
      gstNumber: "",
      companyAddress: "",
      companyCity: "",
      brandName: "",
      website: "",
      instagramLink: "",
      companyDetails: "",
      preferredLanguage: "",
      industryCategory: "",
      targetAudience: [],
      tone: "",
      voicePreference: "",
      tagline: "",
      keywords: "",
      restrictions: "",
      jingleRequirements: "",
      salesPerson: "",
    },
  });

  const targetAudienceOptions = ["Children", "Teenagers", "Adults", "Seniors"];
  const industryCategories = [
    "Food & Beverage",
    "Technology",
    "Fashion",
    "Healthcare",
    "Education",
    "Real Estate",
    "Automotive",
    "Entertainment",
    "Retail",
    "Finance",
    "Travel & Tourism",
    "Others",
  ];
  const toneOptions = ["Fast-paced and energetic", "Slow and calming"];
  const voiceOptions = ["Male", "Female"];

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Link copied to clipboard!");

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy link");
    }
  };

  const handleCloseLinkPopup = () => {
    setShowLinkPopup(false);
    setClientFormLink("");
    setCopied(false);
    onSuccess?.();
    onClose?.();
  };

  const onSubmitForm = async (data) => {
    setIsLoading(true);

    const keywordsArr = (data.keywords || "")
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);

    const targetAudienceArr = Array.isArray(data.targetAudience)
      ? data.targetAudience
      : (data.targetAudience || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);

    const payload = {
      name: data.name,
      email: data.emailAddress,
      mobile_number: data.mobileNumber,
      company_name: data.companyName || null,
      gst_number: data.gstNumber || null,
      company_address: data.companyAddress || null,
      company_city: data.companyCity || null,
      brand_name_in_jingle: data.brandName || null,
      website: data.website || null,
      instagram_link: data.instagramLink || null,
      company_details: data.companyDetails || null,
      preferred_language: data.preferredLanguage || null,
      brand_category: data.industryCategory || null,
      target_audience: targetAudienceArr.length ? targetAudienceArr : null,
      tone: data.tone || null,
      voice_preference: data.voicePreference || null,
      tagline_or_slogan: data.tagline || null,
      keywords_to_highlight: keywordsArr.length ? keywordsArr : null,
      restrictions: data.restrictions || null,
      jingle_requirements: data.jingleRequirements || null,
      sales_person: data.salesPerson || "",
      song_duration:data.songDuration || "",
    };

    try {
      const res = await dispatch(createManualOrder(payload)).unwrap();

      if (res?.success) {
        if (res.client_form_link) {
          setClientFormLink(res.client_form_link);
          setShowLinkPopup(true);
          toast.success("Order created successfully!");
        } else {
          toast.success("Order created!");
          await onSuccess?.();
          onClose?.();
        }
      } else {
        toast.error(res?.detail || "Failed to create order");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (adminLoading)
    return (
      <div className="text-center py-8">
        <Loader className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
      </div>
    );

  return (
    <>
      {/* Link Popup Modal */}
      {showLinkPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Link className="w-5 h-5 text-orange-600" />
                Client Form Link
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCloseLinkPopup}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-3">
                  Order created successfully! Share this link with the client to complete their
                  details:
                </p>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Client Form URL
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm break-all">
                      {clientFormLink}
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(clientFormLink)}
                      className="px-3 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded border border-orange-200 flex items-center gap-1.5 text-sm font-medium"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-xs text-blue-800">
                    <span className="font-semibold">Note:</span> The client must fill and submit
                    the form before you can proceed with jingle production. Once submitted, the
                    form will be locked for editing.
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseLinkPopup}
                  className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Form Modal */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 z-50">
        <div
          className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[95vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Music className="w-4 h-4 text-orange-600" />
              New Jingle Order
            </h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmitForm)} className="p-4">
            <div className="space-y-6">
              {/* Basic Information Section */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Name *
                    </label>
                    <input
                      type="text"
                      {...register("name", {
                        required: "Name is required",
                        minLength: { value: 2, message: "Minimum 2 characters" },
                      })}
                      className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                        errors.name ? "border-red-300 bg-red-50" : "border-gray-300"
                      }`}
                      placeholder="Full name"
                      disabled={isLoading}
                    />
                    {errors.name && (
                      <p className="text-red-600 text-xs mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      {...register("mobileNumber", {
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[\d\s\-\(\)+]{10,}$/,
                          message: "Valid mobile number required",
                        },
                      })}
                      className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                        errors.mobileNumber ? "border-red-300 bg-red-50" : "border-gray-300"
                      }`}
                      placeholder="Mobile number"
                      disabled={isLoading}
                    />
                    {errors.mobileNumber && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.mobileNumber.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register("emailAddress", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Valid email required",
                        },
                      })}
                      className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                        errors.emailAddress ? "border-red-300 bg-red-50" : "border-gray-300"
                      }`}
                      placeholder="Email address"
                      disabled={isLoading}
                    />
                    {errors.emailAddress && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.emailAddress.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Sales Person *
                    </label>
                    <Controller
                      name="salesPerson"
                      control={control}
                      rules={{ required: "Sales Person is required" }}
                      render={({ field }) => (
                        <>
                          <select
                            {...field}
                            className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                              errors.salesPerson ? "border-red-300 bg-red-50" : "border-gray-300"
                            }`}
                            disabled={isLoading}
                          >
                            <option value="">Select sales person</option>
                            {usersListResponse?.sales_persons?.map((person) => (
                              <option key={person.user_pk} value={person.user_pk}>
                                {person.first_name + " " + person.last_name}
                              </option>
                            ))}
                          </select>
                          {errors.salesPerson && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.salesPerson.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Song Duration *
                    </label>
                    <Controller
                      name="songDuration"
                      control={control}
                      rules={{ required: "Song Duration is required" }}
                      render={({ field }) => (
                        <>
                          <select
                            {...field}
                            className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 disabled:bg-gray-100 ${
                              errors.songDuration ? "border-red-300 bg-red-50" : "border-gray-300"
                            }`}
                            disabled={isLoading}
                          >
                            <option value="">Select Song Duration</option>
                            {songDuration?.map((duration,i )=> (
                              <option key={i} value={duration}>
                                {duration }
                              </option>
                            ))}
                          </select>
                          {errors.songDuration && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.songDuration.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* Company Information Accordion */}
              <Accordion
                title="Company Information"
                isOpen={accordionStates.companyInfo}
                onToggle={() => toggleAccordion("companyInfo")}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Company Name
                    </label>
                    <input
                      type="text"
                      {...register("companyName")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Company name"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      GST Number
                    </label>
                    <input
                      type="text"
                      {...register("gstNumber")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="GST number"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Company City
                    </label>
                    <input
                      type="text"
                      {...register("companyCity")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="City"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Company Address
                    </label>
                    <textarea
                      {...register("companyAddress")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Company address"
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </Accordion>

              {/* Brand Information Accordion */}
              <Accordion
                title="Brand Information"
                isOpen={accordionStates.brandInfo}
                onToggle={() => toggleAccordion("brandInfo")}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Brand Name for Jingle
                    </label>
                    <input
                      type="text"
                      {...register("brandName")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Brand name for jingle"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Website
                    </label>
                    <input
                      type="text"
                      {...register("website")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="example.com"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Instagram Link
                    </label>
                    <input
                      type="text"
                      {...register("instagramLink")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="instagram.com/page"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Company Details
                    </label>
                    <textarea
                      {...register("companyDetails")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="About company, products, vision..."
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </Accordion>

              {/* Jingle Requirements Accordion */}
              <Accordion
                title="Jingle Requirements"
                isOpen={accordionStates.jingleRequirements}
                onToggle={() => toggleAccordion("jingleRequirements")}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Preferred Language
                    </label>
                    <input
                      type="text"
                      {...register("preferredLanguage")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="e.g., English, Hindi"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Industry Category
                    </label>
                    <Controller
                      name="industryCategory"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                          disabled={isLoading}
                        >
                          <option value="">Select category</option>
                          {industryCategories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Target Audience
                    </label>
                    <Controller
                      name="targetAudience"
                      control={control}
                      render={({ field }) => (
                        <div className="relative" ref={dropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className={`w-full px-2.5 py-1.5 border rounded text-sm text-left focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                              errors.targetAudience
                                ? "border-red-300 bg-red-50"
                                : "border-gray-300"
                            } ${isLoading ? "bg-gray-100" : ""}`}
                            disabled={isLoading}
                          >
                            <div className="flex justify-between items-center">
                              <span>
                                {field.value?.length > 0
                                  ? field.value.join(", ")
                                  : "Select target audience"}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  isDropdownOpen ? "rotate-180" : ""
                                }`}
                              />
                            </div>
                          </button>

                          {isDropdownOpen && (
                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg">
                              {targetAudienceOptions.map((option) => (
                                <label
                                  key={option}
                                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={field.value?.includes(option)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        field.onChange([...field.value, option]);
                                      } else {
                                        field.onChange(
                                          field.value.filter((value) => value !== option)
                                        );
                                      }
                                    }}
                                    className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">{option}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    />
                    {errors.targetAudience && (
                      <p className="text-red-600 text-xs mt-1">
                        {errors.targetAudience.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Tone
                    </label>
                    <Controller
                      name="tone"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                          disabled={isLoading}
                        >
                          <option value="">Select tone</option>
                          {toneOptions.map((tone) => (
                            <option key={tone} value={tone}>
                              {tone}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Voice Preference
                    </label>
                    <Controller
                      name="voicePreference"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                          disabled={isLoading}
                        >
                          <option value="">Select voice</option>
                          {voiceOptions.map((voice) => (
                            <option key={voice} value={voice}>
                              {voice}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Tagline or Slogan
                    </label>
                    <input
                      type="text"
                      {...register("tagline")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Your tagline or slogan"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Keywords (comma separated)
                    </label>
                    <textarea
                      {...register("keywords")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="fast, affordable, reliable"
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Jingle Requirements
                    </label>
                    <textarea
                      {...register("jingleRequirements")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Music style, mood, instruments, references..."
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </Accordion>

              {/* Production Details Accordion */}
              <Accordion
                title="Production Details"
                isOpen={accordionStates.productionDetails}
                onToggle={() => toggleAccordion("productionDetails")}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Restrictions
                    </label>
                    <textarea
                      {...register("restrictions")}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      placeholder="Language, cultural, or other restrictions"
                      rows="2"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </Accordion>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-2 mt-6 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full inline-block mr-1.5"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Music className="w-3.5 h-3.5 inline-block mr-1.5" />
                    Create Order
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default JingleOrderForm;